import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UiLabel } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Equipe() {
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([api.get("/employees"), api.get("/settings")]);
      setEmployees(e.data); setSettings(s.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (emp) => {
    setEditing(emp);
    setF(emp ? { ...emp } : { name: "", role: "", salaire: "", charge_rate: settings?.social_charge_rate ?? 0.3158, payout_day: 25, active: true });
    setDialog(true);
  };

  const save = async () => {
    if (!f.name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/employees/${editing.id}`, f);
      else await api.post("/employees", f);
      toast.success("Enregistré");
      setDialog(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const del = async () => { await api.delete(`/employees/${confirm.id}`); toast.success("Supprimé"); setConfirm(null); load(); };

  const active = employees.filter((e) => e.active);
  const totalSal = active.reduce((s, e) => s + e.salaire, 0);
  const totalCharges = active.reduce((s, e) => s + e.charges, 0);
  const totalCout = active.reduce((s, e) => s + e.cout_employeur, 0);
  const rate = f.charge_rate ?? 0.3158;
  const sal = parseFloat(f.salaire) || 0;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Masse salariale</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Équipe & Salaires</h1>
        </div>
        <Button data-testid="add-employee-btn" onClick={() => open(null)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1.5 h-4 w-4" />Salarié</Button>
      </div>

      {loading ? <Loader /> : (
        <div className="mt-5 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            <div className={`${CARD} p-5`}><Label>Salaires nets</Label><p className="mt-1 font-display text-xl font-black">{fmtEUR(totalSal)}</p></div>
            <div className={`${CARD} p-5`}><Label>Charges sociales</Label><p className="mt-1 font-display text-xl font-black text-amber-600">{fmtEUR(totalCharges)}</p></div>
            <div className={`${CARD} p-5`}><Label>Coût employeur</Label><p className="mt-1 font-display text-xl font-black text-rose-600">{fmtEUR(totalCout)}</p></div>
          </div>

          <div className={`${CARD} p-4 sm:p-6`}>
            {employees.length === 0 ? <EmptyState title="Aucun salarié" subtitle="Ajoutez les membres de votre équipe." />
              : employees.map((e) => (
                <div key={e.id} className="flex items-center gap-3 border-b border-gray-50 py-3 last:border-0" data-testid={`emp-${e.id}`}>
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full font-bold ${e.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>{e.name.charAt(0)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{e.name} {!e.active && <span className="text-xs text-gray-400">(inactif)</span>}</p>
                    <p className="text-xs text-gray-400">{e.role} · charges {(e.charge_rate * 100).toFixed(1)}% · paie le {e.payout_day ?? 25}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-sm font-bold">{fmtEUR(e.salaire)}</p>
                    <p className="text-xs text-gray-400">coût {fmtEUR(e.cout_employeur)}</p>
                  </div>
                  <button onClick={() => open(e)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setConfirm(e)} className="rounded-lg p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
          </div>
          <p className="text-xs text-gray-400">Taux de charges par défaut : {((settings?.social_charge_rate ?? 0.3158) * 100).toFixed(2)}% (modifiable dans Paramètres, ou par salarié).</p>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="employee-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Modifier" : "Nouveau salarié"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><UiLabel>Nom</UiLabel><Input data-testid="emp-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div><UiLabel>Fonction</UiLabel><Input value={f.role || ""} onChange={(e) => setF({ ...f, role: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><UiLabel>Salaire net (€)</UiLabel><Input data-testid="emp-salary" type="number" value={f.salaire ?? ""} onChange={(e) => setF({ ...f, salaire: e.target.value })} className="mt-1.5 rounded-xl" /></div>
              <div><UiLabel>Taux charges (%)</UiLabel><Input type="number" step="0.01" value={((f.charge_rate ?? 0.3158) * 100).toFixed(2)} onChange={(e) => setF({ ...f, charge_rate: (parseFloat(e.target.value) || 0) / 100 })} className="mt-1.5 rounded-xl" /></div>
            </div>
            <div>
              <UiLabel>Jour de décaissement</UiLabel>
              <Input data-testid="emp-payout-day" type="number" min="1" max="28" value={f.payout_day ?? 25} onChange={(e) => setF({ ...f, payout_day: e.target.value })} className="mt-1.5 rounded-xl" />
              <p className="mt-1 text-xs text-gray-400">Jour du mois où le salaire sort du compte (1 à 28).</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Charges estimées</span><b>{fmtEUR(sal * rate)}</b></div>
              <div className="flex justify-between"><span className="text-gray-500">Coût employeur</span><b>{fmtEUR(sal * (1 + rate))}</b></div>
            </div>
            <div className="flex items-center justify-between"><UiLabel>Actif</UiLabel><Switch checked={f.active ?? true} onCheckedChange={(v) => setF({ ...f, active: v })} /></div>
            <Button data-testid="emp-submit" onClick={save} disabled={saving} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enregistrer"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader><AlertDialogTitle>Supprimer {confirm?.name} ?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="rounded-full bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
